import React, { useState } from 'react';
import { Plus, Edit3, Trash2, Barcode, Printer, X, Package, Check, AlertCircle } from 'lucide-react';

const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000/api';

/**
 * High-Precision Vector SVG Barcode Renderer (Code 128 / EAN standard representation)
 * Renders authentic crisp alternating width bars with guard patterns and human-readable text.
 */
function BarcodeSVG({ value = '', width = 240, height = 65, showText = true }) {
  const cleanVal = (value || '000000000000').toString().toUpperCase();
  
  // Deterministic bar pattern based on characters
  const getBars = (str) => {
    // Standard start guard: 101
    let pattern = [2, 1, 2, 1];
    for (let i = 0; i < str.length; i++) {
      const code = str.charCodeAt(i);
      // Generate 6 alternating bar/space widths between 1 and 4
      const b1 = ((code * 3 + 1) % 3) + 1;
      const s1 = ((code * 2 + 2) % 3) + 1;
      const b2 = ((code * 5 + 3) % 4) + 1;
      const s2 = ((code * 7 + 1) % 3) + 1;
      pattern.push(b1, s1, b2, s2);
    }
    // End guard
    pattern.push(2, 1, 2, 2, 1);
    return pattern;
  };

  const bars = getBars(cleanVal);
  const totalUnits = bars.reduce((a, b) => a + b, 0);
  const unitWidth = (width - 20) / totalUnits;

  let currentX = 10;
  const rects = [];

  bars.forEach((w, idx) => {
    const isBar = idx % 2 === 0;
    const barW = w * unitWidth;
    if (isBar) {
      rects.push(
        <rect
          key={idx}
          x={currentX}
          y={4}
          width={Math.max(1, barW)}
          height={height - 20}
          fill="#000000"
        />
      );
    }
    currentX += barW;
  });

  return (
    <div style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'center', background: '#ffffff', padding: '6px 10px', borderRadius: 6 }}>
      <svg width={width} height={height - (showText ? 0 : 18)} viewBox={`0 0 ${width} ${height - (showText ? 0 : 18)}`} style={{ display: 'block' }}>
        {rects}
      </svg>
      {showText && (
        <div style={{
          fontFamily: "'Courier New', Courier, monospace",
          fontSize: '0.82rem',
          fontWeight: 800,
          letterSpacing: '0.18em',
          color: '#000000',
          marginTop: 2
        }}>
          {cleanVal}
        </div>
      )}
    </div>
  );
}

export default function ProductCatalog({ 
  products = [], 
  onSaveProduct, 
  onDeleteProduct, 
  currentTenant, 
  lang = 'mr',
  features
}) {
  const isMr = lang === 'mr';
  const effectiveFeatures = features || currentTenant?.features || currentTenant?.feature || {};
  const isDairyEnabled = Boolean(effectiveFeatures.dairy_byproducts);
  const isBakeryEnabled = Boolean(effectiveFeatures.bakery_products);
  const isMilkOnly = !isDairyEnabled && !isBakeryEnabled;
  const tenantSlug = currentTenant?.slug || (() => {
    try {
      const saved = localStorage.getItem('milkpro_tenant_data');
      return saved ? JSON.parse(saved)?.slug : 'samarth-krupa-dairy';
    } catch {
      return 'samarth-krupa-dairy';
    }
  })();

  const [selectedCategory, setSelectedCategory] = useState('ALL');
  const [modalMode, setModalMode] = useState(null); // 'add' | 'edit'
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [deleteConfirmProd, setDeleteConfirmProd] = useState(null);
  const [barcodeModalProd, setBarcodeModalProd] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [statusMsg, setStatusMsg] = useState(null);

  // Form State
  const initialFormState = {
    code: '',
    barcode: '',
    name_mr: '',
    name_en: '',
    category: 'Milk',
    unit_mr: 'लिटर',
    unit_en: 'Litre',
    packing_type: 'पिशवी / बाटली',
    mrp: '',
    retail_rate: '',
    wholesale_rate: '',
    hsn_code: '0401',
    cgst_pct: '0.00',
    sgst_pct: '0.00',
    in_stock: '100'
  };
  const [formData, setFormData] = useState(initialFormState);

  // Open Add Modal
  const handleOpenAdd = () => {
    const milkProductsCount = products.filter(p => (p.category || '').toLowerCase() === 'milk').length;
    if (isMilkOnly && milkProductsCount >= 2) {
      alert(isMr 
        ? 'फक्त दूध वितरण प्रणालीमध्ये गाय दूध आणि म्हैस दूध ही २ उत्पादने आधीच समाविष्ट आहेत. दर बदलण्यासाठी कृपया संपादन (Edit) बटण वापरा.' 
        : 'For pure milk delivery, maximum 2 products (Cow Milk & Buffalo Milk) already exist. Please use Edit to update rates.');
      return;
    }

    setFormData({
      ...initialFormState,
      code: 'PROD-' + (products.length + 1)
    });
    setModalMode('add');
    setSelectedProduct(null);
  };

  // Open Edit Modal
  const handleOpenEdit = (p) => {
    setFormData({
      code: p.code || '',
      barcode: p.barcode || '',
      name_mr: p.name_mr || p.name || '',
      name_en: p.name_en || p.nameEn || '',
      category: p.category || 'Milk',
      unit_mr: p.unit_mr || p.unit || 'लिटर',
      unit_en: p.unit_en || p.unitEn || 'Litre',
      packing_type: p.packing_type || p.packingType || '',
      mrp: p.mrp ? p.mrp.toString() : '',
      retail_rate: (p.retail_rate !== undefined ? p.retail_rate : p.retailRate || '').toString(),
      wholesale_rate: (p.wholesale_rate !== undefined ? p.wholesale_rate : p.wholesaleRate || '').toString(),
      hsn_code: p.hsn_code || p.hsn || '0401',
      cgst_pct: (p.cgst_pct || 0).toString(),
      sgst_pct: (p.sgst_pct || 0).toString(),
      in_stock: (p.in_stock !== undefined ? p.in_stock : 100).toString()
    });
    setSelectedProduct(p);
    setModalMode('edit');
  };

  // Submit Add / Edit Form
  const handleSubmitForm = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setStatusMsg(null);

    const payload = {
      code: formData.code.trim().toUpperCase(),
      barcode: formData.barcode.trim() || null, // Backend auto-generates if null
      name_mr: formData.name_mr.trim(),
      name_en: formData.name_en.trim() || formData.name_mr.trim(),
      category: formData.category,
      unit_mr: formData.unit_mr,
      unit_en: formData.unit_en,
      packing_type: formData.packing_type,
      mrp: parseFloat(formData.mrp) || parseFloat(formData.retail_rate) || 0,
      retail_rate: parseFloat(formData.retail_rate) || 0,
      wholesale_rate: parseFloat(formData.wholesale_rate) || parseFloat(formData.retail_rate) || 0,
      hsn_code: formData.hsn_code || '0401',
      cgst_pct: parseFloat(formData.cgst_pct) || 0,
      sgst_pct: parseFloat(formData.sgst_pct) || 0,
      in_stock: parseInt(formData.in_stock, 10) || 0
    };

    try {
      const url = modalMode === 'add'
        ? `${API_BASE}/tenant/${tenantSlug}/products`
        : `${API_BASE}/tenant/${tenantSlug}/products/${selectedProduct.id}`;
      
      const method = modalMode === 'add' ? 'POST' : 'PUT';

      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const data = await res.json();

      if (data.success) {
        setStatusMsg({ type: 'success', text: data.message });
        if (onSaveProduct) {
          onSaveProduct(data.product);
        }
        setModalMode(null);
      } else {
        setStatusMsg({ type: 'error', text: data.message || 'उत्पादन सेव्ह करता आले नाही.' });
      }
    } catch (err) {
      console.error('Error saving product:', err);
      setStatusMsg({ type: 'error', text: 'बॅकएंड सर्व्हर एरर: ' + err.message });
    } finally {
      setSubmitting(false);
    }
  };

  // Delete Product
  const handleDeleteProduct = async () => {
    if (!deleteConfirmProd) return;
    setSubmitting(true);
    try {
      const res = await fetch(`${API_BASE}/tenant/${tenantSlug}/products/${deleteConfirmProd.id}`, {
        method: 'DELETE'
      });
      const data = await res.json();
      if (data.success) {
        setStatusMsg({ type: 'success', text: data.message });
        if (onDeleteProduct) {
          onDeleteProduct(deleteConfirmProd.id);
        }
        setDeleteConfirmProd(null);
      } else {
        setStatusMsg({ type: 'error', text: data.message });
      }
    } catch (err) {
      console.error('Error deleting product:', err);
      setStatusMsg({ type: 'error', text: 'उत्पादन डिलीट करता आले नाही: ' + err.message });
    } finally {
      setSubmitting(false);
    }
  };

  const filteredProducts = products.filter(p => {
    if (selectedCategory === 'ALL') return true;
    return (p.category || 'Milk').toLowerCase() === selectedCategory.toLowerCase();
  });

  return (
    <div style={{ maxWidth: 1440, margin: '0 auto', padding: '1.25rem', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      
      {/* Toast Alert */}
      {statusMsg && (
        <div style={{
          padding: '0.75rem 1rem',
          borderRadius: 8,
          fontSize: '0.85rem',
          fontWeight: 700,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          background: statusMsg.type === 'success' ? '#dcfce7' : '#fee2e2',
          color: statusMsg.type === 'success' ? '#166534' : '#991b1b',
          border: `1px solid ${statusMsg.type === 'success' ? '#86efac' : '#fca5a5'}`
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            {statusMsg.type === 'success' ? <Check size={16} /> : <AlertCircle size={16} />}
            <span>{statusMsg.text}</span>
          </div>
          <button onClick={() => setStatusMsg(null)} style={{ background: 'transparent', border: 'none', cursor: 'pointer' }}><X size={15} /></button>
        </div>
      )}

      {/* Header with Add Product CTA */}
      <div style={{
        background: '#ffffff',
        borderRadius: 14,
        padding: '1rem 1.25rem',
        border: '1px solid #e2e8f0',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: '0.75rem'
      }}>
        <div>
          <h2 style={{ fontSize: '1.25rem', fontWeight: 800, color: '#0f172a', margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
            <Package size={22} color="#0284c7" />
            <span>{isMr ? 'उत्पादने, दर व बारकोड कॅटलॉग' : 'Product Catalog & Barcode Management'}</span>
          </h2>
          <div style={{ fontSize: '0.75rem', color: '#64748b', marginTop: 2 }}>
            {isMr ? 'डेअरीतील दुग्धजन्य उत्पादने, विक्री दर आणि ऑटोमॅटिक युनिक सिस्टिम बारकोड' : 'Dairy products, pricing and automatic unique system barcodes'}
          </div>
        </div>

        <button
          onClick={handleOpenAdd}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            padding: '0.6rem 1.2rem',
            borderRadius: 8,
            background: 'linear-gradient(135deg, #0284c7 0%, #0369a1 100%)',
            color: '#fff',
            fontWeight: 800,
            fontSize: '0.85rem',
            border: 'none',
            cursor: 'pointer',
            boxShadow: '0 4px 8px rgba(2, 132, 199, 0.25)'
          }}
        >
          <Plus size={16} />
          <span>{isMr ? '+ नवीन उत्पादन जोडा' : '+ Add New Product'}</span>
        </button>
      </div>

      {/* Category Tabs */}
      {isMilkOnly ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <div style={{
            padding: '0.45rem 1.1rem',
            borderRadius: 20,
            fontSize: '0.8rem',
            fontWeight: 700,
            border: '1px solid #0284c7',
            background: '#e0f2fe',
            color: '#0369a1',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6
          }}>
            <span>🥛 {isMr ? 'फक्त दूध उत्पादने (गाय दूध व म्हैस दूध - कमाल २ उत्पादने)' : 'Pure Milk Products Only (Max 2 Products)'}</span>
          </div>
        </div>
      ) : (
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
          {[
            { key: 'ALL', labelMr: 'सर्व उत्पादने', labelEn: 'All Products' },
            { key: 'Milk', labelMr: 'दूध (Milk)', labelEn: 'Milk' },
            ...(isDairyEnabled ? [{ key: 'Dairy', labelMr: 'दुग्धजन्य (Dairy Byproducts)', labelEn: 'Dairy' }] : []),
            ...(isBakeryEnabled ? [{ key: 'Bakery', labelMr: 'बेकरी व इतर', labelEn: 'Bakery & Other' }] : []),
          ].map(tab => (
            <button
              key={tab.key}
              onClick={() => setSelectedCategory(tab.key)}
              style={{
                padding: '0.45rem 1rem',
                borderRadius: 20,
                fontSize: '0.8rem',
                fontWeight: 700,
                border: selectedCategory === tab.key ? '1px solid #0284c7' : '1px solid #cbd5e1',
                background: selectedCategory === tab.key ? '#e0f2fe' : '#ffffff',
                color: selectedCategory === tab.key ? '#0369a1' : '#475569',
                cursor: 'pointer'
              }}
            >
              {isMr ? tab.labelMr : tab.labelEn}
            </button>
          ))}
        </div>
      )}

      {/* Products Grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(310px, 1fr))', gap: '1rem' }}>
        {filteredProducts.map(p => {
          const prodName = isMr ? (p.name_mr || p.name) : (p.name_en || p.nameEn || p.name_mr || p.name);
          const retailRate = p.retail_rate !== undefined ? p.retail_rate : p.retailRate;
          const unitName = isMr ? (p.unit_mr || p.unit || 'लिटर') : (p.unit_en || p.unitEn || 'Litre');
          const packing = p.packing_type || p.packingType || '';
          const barcodeStr = p.barcode || ('89006000000' + p.id);

          return (
            <div key={p.id} style={{
              background: '#ffffff',
              borderRadius: 14,
              padding: '1.25rem',
              border: '1px solid #e2e8f0',
              boxShadow: '0 2px 5px rgba(0,0,0,0.03)',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              transition: 'transform 0.15s ease, box-shadow 0.15s ease'
            }}>
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{
                    fontSize: '0.72rem',
                    fontWeight: 800,
                    background: '#f0f9ff',
                    color: '#0284c7',
                    border: '1px solid #bae6fd',
                    padding: '2px 8px',
                    borderRadius: 6
                  }}>
                    {p.code}
                  </span>

                  <div style={{ display: 'flex', gap: 4 }}>
                    <button
                      onClick={() => setBarcodeModalProd(p)}
                      title={isMr ? "सिस्टिम बारकोड लेबल प्रिंट करा" : "Print Barcode label"}
                      style={{
                        background: '#f8fafc',
                        border: '1px solid #cbd5e1',
                        borderRadius: 6,
                        padding: '3px 8px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 4,
                        fontSize: '0.72rem',
                        color: '#0f172a',
                        fontWeight: 700
                      }}
                    >
                      <Barcode size={14} color="#0284c7" />
                      <span>{isMr ? 'बारकोड लेबल' : 'Barcode Label'}</span>
                    </button>

                    <button
                      onClick={() => handleOpenEdit(p)}
                      title={isMr ? "बदल करा" : "Edit"}
                      style={{
                        background: '#eff6ff',
                        border: '1px solid #bfdbfe',
                        color: '#1d4ed8',
                        borderRadius: 6,
                        padding: '3px 7px',
                        cursor: 'pointer'
                      }}
                    >
                      <Edit3 size={13} />
                    </button>

                    <button
                      onClick={() => setDeleteConfirmProd(p)}
                      title={isMr ? "हटवा" : "Delete"}
                      style={{
                        background: '#fef2f2',
                        border: '1px solid #fecaca',
                        color: '#dc2626',
                        borderRadius: 6,
                        padding: '3px 7px',
                        cursor: 'pointer'
                      }}
                    >
                      <Trash2 size={13} />
                    </button>
                  </div>
                </div>

                <h3 style={{ fontSize: '1.1rem', fontWeight: 800, color: '#0f172a', margin: '0.65rem 0 0.25rem 0' }}>
                  {prodName}
                </h3>
                
                <div style={{ fontSize: '0.75rem', color: '#64748b' }}>
                  {packing && `${packing} • `} HSN: {p.hsn_code || p.hsn || '0401'}
                </div>

                {/* Prominent Barcode Box */}
                <div style={{
                  marginTop: '0.65rem',
                  padding: '0.4rem 0.65rem',
                  background: '#f8fafc',
                  border: '1px solid #e2e8f0',
                  borderRadius: 8,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <Barcode size={18} color="#475569" />
                    <div>
                      <div style={{ fontSize: '0.62rem', color: '#64748b', fontWeight: 700, textTransform: 'uppercase' }}>
                        {isMr ? 'सिस्टिम बारकोड' : 'System Barcode'}
                      </div>
                      <div style={{ fontFamily: 'monospace', fontWeight: 800, fontSize: '0.85rem', color: '#0f172a' }}>
                        {barcodeStr}
                      </div>
                    </div>
                  </div>

                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: '0.62rem', color: '#64748b', fontWeight: 700 }}>
                      {isMr ? 'उपलब्ध साठा' : 'Stock'}
                    </div>
                    <div style={{ fontWeight: 800, fontSize: '0.85rem', color: '#059669' }}>
                      {p.in_stock !== undefined ? p.in_stock : 100}
                    </div>
                  </div>
                </div>
              </div>

              <div style={{ marginTop: '1rem', paddingTop: '0.85rem', borderTop: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
                <div>
                  <div style={{ fontSize: '0.68rem', color: '#64748b', fontWeight: 700, textTransform: 'uppercase' }}>
                    {isMr ? 'किरकोळ विक्री दर' : 'Retail Rate'}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 4 }}>
                    <span style={{ fontSize: '1.35rem', fontWeight: 900, color: '#0f172a' }}>₹{retailRate}</span>
                    <span style={{ fontSize: '0.75rem', color: '#64748b' }}>/{unitName}</span>
                    {p.mrp && parseFloat(p.mrp) > parseFloat(retailRate) && (
                      <span style={{ fontSize: '0.72rem', color: '#94a3b8', textDecoration: 'line-through', marginLeft: 4 }}>
                        MRP ₹{p.mrp}
                      </span>
                    )}
                  </div>
                </div>

                {p.wholesale_rate && parseFloat(p.wholesale_rate) > 0 && (
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: '0.65rem', color: '#64748b' }}>{isMr ? 'घाऊक दर' : 'Wholesale'}</div>
                    <div style={{ fontSize: '0.85rem', fontWeight: 800, color: '#0369a1' }}>₹{p.wholesale_rate}</div>
                  </div>
                )}
              </div>

            </div>
          );
        })}
      </div>

      {/* ADD / EDIT PRODUCT MODAL FORM */}
      {modalMode && (
        <div style={{
          position: 'fixed',
          top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(15, 23, 42, 0.6)',
          backdropFilter: 'blur(3px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 100,
          padding: '1rem'
        }}>
          <div style={{
            background: '#ffffff',
            borderRadius: 16,
            maxWidth: 580,
            width: '100%',
            maxHeight: '90vh',
            overflowY: 'auto',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)'
          }}>
            <div style={{
              background: '#0284c7',
              color: '#fff',
              padding: '1rem 1.25rem',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <div>
                <span style={{ fontWeight: 800, fontSize: '1.05rem' }}>
                  {modalMode === 'add' 
                    ? (isMr ? 'नवीन उत्पादन नोंदणी (Add Product)' : 'Add New Product')
                    : (isMr ? 'उत्पादनाची माहिती बदला (Edit Product)' : 'Edit Product')}
                </span>
                <div style={{ fontSize: '0.75rem', color: '#e0f2fe' }}>
                  {isMr ? 'तपशील भरा व कॅटलॉगमध्ये सेव्ह करा' : 'Enter product details to save in catalog'}
                </div>
              </div>
              <button onClick={() => setModalMode(null)} style={{ background: 'transparent', border: 'none', color: '#fff', cursor: 'pointer' }}>
                <X size={18} />
              </button>
            </div>

            <form onSubmit={handleSubmitForm} style={{ padding: '1.25rem', display: 'flex', flexDirection: 'column', gap: '0.9rem' }}>
              
              {/* Product Code & Category */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 700, color: '#334155', marginBottom: 4 }}>
                    {isMr ? 'उत्पादन कोड (Code) *' : 'Product Code *'}
                  </label>
                  <input
                    required
                    type="text"
                    placeholder="उदा. COW-MILK-1L"
                    value={formData.code}
                    onChange={e => setFormData({ ...formData, code: e.target.value })}
                    style={{ width: '100%', padding: '0.5rem', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: '0.85rem', boxSizing: 'border-box' }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 700, color: '#334155', marginBottom: 4 }}>
                    {isMr ? 'वर्गवारी (Category)' : 'Category'}
                  </label>
                  <select
                    value={formData.category}
                    onChange={e => setFormData({ ...formData, category: e.target.value })}
                    style={{ width: '100%', padding: '0.5rem', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: '0.85rem', background: '#fff', boxSizing: 'border-box' }}
                  >
                    <option value="Milk">{isMr ? 'दूध (Milk)' : 'Milk'}</option>
                    {isDairyEnabled && (
                      <option value="Dairy">{isMr ? 'दुग्धजन्य (Dairy Byproducts)' : 'Dairy Byproducts'}</option>
                    )}
                    {isBakeryEnabled && (
                      <option value="Bakery">{isMr ? 'बेकरी व इतर (Bakery & Other)' : 'Bakery & Other'}</option>
                    )}
                  </select>
                </div>
              </div>

              {/* Barcode Field (User can enter or leave empty for auto-generation) */}
              <div style={{ background: '#f8fafc', padding: '0.75rem', borderRadius: 10, border: '1px solid #e2e8f0' }}>
                <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 700, color: '#0f172a', marginBottom: 4 }}>
                  {isMr ? 'कंपनी बारकोड (Barcode) - पर्यायी:' : 'Product Barcode (Optional):'}
                </label>
                <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                  <Barcode size={20} color="#0284c7" />
                  <input
                    type="text"
                    placeholder={isMr ? "उदा. 890123456789 (स्कॅन करा किंवा टाईप करा)" : "e.g. 890123456789"}
                    value={formData.barcode}
                    onChange={e => setFormData({ ...formData, barcode: e.target.value })}
                    style={{ flex: 1, padding: '0.45rem 0.65rem', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: '0.85rem', fontFamily: 'monospace', fontWeight: 700 }}
                  />
                </div>
                <div style={{ fontSize: '0.7rem', color: '#64748b', marginTop: 4 }}>
                  {isMr 
                    ? '💡 टीप: जर कंपनीचा बारकोड नसेल तर रिक्त ठेवा; सिस्टिम आपोआप युनिक १२-अंकी बारकोड तयार करेल.'
                    : '💡 Note: If left empty, system automatically generates a unique 12-digit barcode.'}
                </div>
              </div>

              {/* Names: Marathi & English */}
              <div>
                <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 700, color: '#334155', marginBottom: 4 }}>
                  {isMr ? 'मराठीत नाव (Product Name Marathi) *' : 'Marathi Name *'}
                </label>
                <input
                  required
                  type="text"
                  placeholder="उदा. ताजे गाय दूध (१ लिटर पिशवी)"
                  value={formData.name_mr}
                  onChange={e => setFormData({ ...formData, name_mr: e.target.value })}
                  style={{ width: '100%', padding: '0.5rem', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: '0.85rem', boxSizing: 'border-box' }}
                />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 700, color: '#334155', marginBottom: 4 }}>
                  {isMr ? 'इंग्रजीत नाव (Product Name English)' : 'English Name'}
                </label>
                <input
                  type="text"
                  placeholder="e.g. Fresh Cow Milk (1L Pouch)"
                  value={formData.name_en}
                  onChange={e => setFormData({ ...formData, name_en: e.target.value })}
                  style={{ width: '100%', padding: '0.5rem', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: '0.85rem', boxSizing: 'border-box' }}
                />
              </div>

              {/* Unit & Packing */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 700, color: '#334155', marginBottom: 4 }}>
                    {isMr ? 'एकक / युनिट (Unit)' : 'Unit'}
                  </label>
                  <select
                    value={formData.unit_mr}
                    onChange={e => {
                      const mr = e.target.value;
                      const enMap = { 'लिटर': 'Litre', 'ग्रॅम': 'Gram', 'किलो': 'Kg', 'कप': 'Cup', 'बाटली': 'Bottle', 'नग': 'Piece' };
                      setFormData({ ...formData, unit_mr: mr, unit_en: enMap[mr] || 'Piece' });
                    }}
                    style={{ width: '100%', padding: '0.5rem', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: '0.85rem', background: '#fff', boxSizing: 'border-box' }}
                  >
                    <option value="लिटर">लिटर (Litre)</option>
                    <option value="ग्रॅम">ग्रॅम (Gram)</option>
                    <option value="किलो">किलो (Kg)</option>
                    <option value="कप">कप (Cup)</option>
                    <option value="बाटली">बाटली (Bottle)</option>
                    <option value="नग">नग / पाकीट (Piece)</option>
                  </select>
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 700, color: '#334155', marginBottom: 4 }}>
                    {isMr ? 'पॅकिंग प्रकार (Packing)' : 'Packing Type'}
                  </label>
                  <input
                    type="text"
                    placeholder="उदा. पिशवी, बाटली, व्हॅक्यूम पॅक"
                    value={formData.packing_type}
                    onChange={e => setFormData({ ...formData, packing_type: e.target.value })}
                    style={{ width: '100%', padding: '0.5rem', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: '0.85rem', boxSizing: 'border-box' }}
                  />
                </div>
              </div>

              {/* Rates: Retail, MRP, Wholesale */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.75rem' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 700, color: '#334155', marginBottom: 4 }}>
                    {isMr ? 'विक्री दर (₹) *' : 'Retail Rate (₹) *'}
                  </label>
                  <input
                    required
                    type="number"
                    step="0.50"
                    placeholder="56.00"
                    value={formData.retail_rate}
                    onChange={e => setFormData({ ...formData, retail_rate: e.target.value })}
                    style={{ width: '100%', padding: '0.5rem', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: '0.85rem', fontWeight: 800, boxSizing: 'border-box' }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 700, color: '#334155', marginBottom: 4 }}>
                    {isMr ? 'एमआरपी (MRP ₹)' : 'MRP (₹)'}
                  </label>
                  <input
                    type="number"
                    step="0.50"
                    placeholder="60.00"
                    value={formData.mrp}
                    onChange={e => setFormData({ ...formData, mrp: e.target.value })}
                    style={{ width: '100%', padding: '0.5rem', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: '0.85rem', boxSizing: 'border-box' }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 700, color: '#334155', marginBottom: 4 }}>
                    {isMr ? 'घाऊक दर (₹)' : 'Wholesale Rate'}
                  </label>
                  <input
                    type="number"
                    step="0.50"
                    placeholder="52.00"
                    value={formData.wholesale_rate}
                    onChange={e => setFormData({ ...formData, wholesale_rate: e.target.value })}
                    style={{ width: '100%', padding: '0.5rem', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: '0.85rem', boxSizing: 'border-box' }}
                  />
                </div>
              </div>

              {/* HSN & In Stock */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 700, color: '#334155', marginBottom: 4 }}>
                    {isMr ? 'HSN कोड' : 'HSN Code'}
                  </label>
                  <input
                    type="text"
                    value={formData.hsn_code}
                    onChange={e => setFormData({ ...formData, hsn_code: e.target.value })}
                    style={{ width: '100%', padding: '0.5rem', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: '0.85rem', boxSizing: 'border-box' }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 700, color: '#334155', marginBottom: 4 }}>
                    {isMr ? 'उपलब्ध साठा (In Stock)' : 'In Stock Quantity'}
                  </label>
                  <input
                    type="number"
                    value={formData.in_stock}
                    onChange={e => setFormData({ ...formData, in_stock: e.target.value })}
                    style={{ width: '100%', padding: '0.5rem', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: '0.85rem', boxSizing: 'border-box' }}
                  />
                </div>
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.65rem', borderTop: '1px solid #e2e8f0', paddingTop: '1rem', marginTop: '0.5rem' }}>
                <button
                  type="button"
                  onClick={() => setModalMode(null)}
                  style={{ background: '#f1f5f9', color: '#475569', border: 'none', padding: '0.6rem 1.1rem', borderRadius: 8, fontWeight: 700, cursor: 'pointer' }}
                >
                  {isMr ? 'रद्द करा' : 'Cancel'}
                </button>

                <button
                  type="submit"
                  disabled={submitting}
                  style={{
                    background: submitting ? '#94a3b8' : '#0284c7',
                    color: '#fff',
                    border: 'none',
                    padding: '0.6rem 1.3rem',
                    borderRadius: 8,
                    fontWeight: 800,
                    cursor: submitting ? 'not-allowed' : 'pointer'
                  }}
                >
                  {submitting ? (isMr ? 'सेव्ह होत आहे...' : 'Saving...') : (isMr ? 'उत्पादन सेव्ह करा' : 'Save Product')}
                </button>
              </div>

            </form>
          </div>
        </div>
      )}

      {/* DELETE CONFIRMATION MODAL */}
      {deleteConfirmProd && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(15, 23, 42, 0.6)',
          backdropFilter: 'blur(3px)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 100, padding: '1rem'
        }}>
          <div style={{ background: '#ffffff', borderRadius: 14, maxWidth: 420, width: '100%', overflow: 'hidden', padding: '1.25rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, color: '#dc2626', marginBottom: '0.75rem' }}>
              <AlertCircle size={24} />
              <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 800 }}>
                {isMr ? 'उत्पादन डिलीट करायचे का?' : 'Delete Product?'}
              </h3>
            </div>
            <p style={{ fontSize: '0.85rem', color: '#475569', lineHeight: 1.5, margin: '0 0 1rem 0' }}>
              {isMr
                ? `तुम्ही खरोखर "${deleteConfirmProd.name_mr || deleteConfirmProd.name}" हे उत्पादन कॅटलॉगमधून काढून टाकू इच्छिता?`
                : `Are you sure you want to delete "${deleteConfirmProd.name_mr || deleteConfirmProd.name}" from the catalog?`}
            </p>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.65rem' }}>
              <button
                type="button"
                onClick={() => setDeleteConfirmProd(null)}
                style={{ background: '#f1f5f9', color: '#475569', border: 'none', padding: '0.55rem 1rem', borderRadius: 8, fontWeight: 700, cursor: 'pointer' }}
              >
                {isMr ? 'रद्द करा' : 'Cancel'}
              </button>
              <button
                type="button"
                disabled={submitting}
                onClick={handleDeleteProduct}
                style={{ background: '#dc2626', color: '#fff', border: 'none', padding: '0.55rem 1.1rem', borderRadius: 8, fontWeight: 800, cursor: 'pointer' }}
              >
                {submitting ? (isMr ? 'काढत आहे...' : 'Deleting...') : (isMr ? 'होय, डिलीट करा' : 'Yes, Delete')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* BARCODE PRINTABLE STICKER LABEL MODAL */}
      {barcodeModalProd && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(3px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100, padding: '1rem' }}>
          <div style={{ background: '#ffffff', borderRadius: 14, maxWidth: 380, width: '100%', overflow: 'hidden', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.2)' }}>
            <div style={{ background: '#0284c7', color: '#fff', padding: '0.85rem 1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontWeight: 800, fontSize: '0.9rem', display: 'flex', alignItems: 'center', gap: 6 }}>
                <Barcode size={18} />
                <span>{isMr ? 'उत्पादन बारकोड लेबल' : 'Product Barcode Label'}</span>
              </span>
              <button onClick={() => setBarcodeModalProd(null)} style={{ background: 'transparent', border: 'none', color: '#fff', cursor: 'pointer' }}><X size={16} /></button>
            </div>

            <div style={{ padding: '1.25rem', textAlign: 'center' }}>
              {/* Sticker Label Preview Box (Standard 50mm x 25mm / 2" x 1" thermal label preview) */}
              <div style={{ border: '2px dashed #0284c7', borderRadius: 10, padding: '1rem', background: '#f8fafc' }}>
                <div style={{ fontSize: '0.72rem', color: '#64748b', fontWeight: 700 }}>
                  {currentTenant?.name || 'समर्थ कृपा डेअरी'}
                </div>
                <div style={{ fontWeight: 900, fontSize: '1.05rem', color: '#0f172a', margin: '2px 0' }}>
                  {barcodeModalProd.name_mr || barcodeModalProd.name}
                </div>
                <div style={{ fontSize: '0.72rem', color: '#475569' }}>
                  {barcodeModalProd.packing_type || barcodeModalProd.packingType}
                </div>
                
                {/* Visual SVG Barcode */}
                <div style={{ margin: '0.75rem auto 0.5rem auto' }}>
                  <BarcodeSVG 
                    value={barcodeModalProd.barcode || ('89006000000' + barcodeModalProd.id)} 
                    width={250} 
                    height={65} 
                    showText={true} 
                  />
                </div>

                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'baseline', gap: 4, marginTop: 4 }}>
                  <span style={{ fontSize: '0.75rem', fontWeight: 700, color: '#64748b' }}>किरकोळ दर:</span>
                  <span style={{ fontWeight: 900, fontSize: '1.3rem', color: '#0f172a' }}>₹{barcodeModalProd.retail_rate || barcodeModalProd.retailRate}</span>
                </div>
                <div style={{ fontSize: '0.65rem', color: '#64748b', marginTop: 2 }}>
                  काऊंटर POS बारकोड गनवर स्कॅन करा
                </div>
              </div>

              <div style={{ marginTop: '1rem', display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                <button onClick={() => setBarcodeModalProd(null)} style={{ padding: '0.45rem 0.85rem', borderRadius: 6, border: '1px solid #cbd5e1', background: '#fff', cursor: 'pointer', fontWeight: 600 }}>
                  {isMr ? 'बंद करा' : 'Close'}
                </button>
                <button onClick={() => window.print()} style={{ padding: '0.45rem 1.1rem', borderRadius: 6, border: 'none', background: '#0284c7', color: '#fff', fontWeight: 800, display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                  <Printer size={15} />
                  <span>{isMr ? 'बारकोड प्रिंट करा' : 'Print Barcode'}</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
